import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IOperationResultNoValue, ResourceAutoCompleteViewModelType } from '@ViewModels';
import { FilesDropTarget } from '@WebComponents/FilesDropTarget';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { RadioButton } from '@WebComponents/RadioButton';
import { SpreadsheetIcon } from '@WebComponents/svgs/icons/SpreadsheetIcon';
import { SvgIcon } from '@WebComponents/svgs/icons/SvgIcon';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { AppImportContactsViewModel } from '../../../../viewmodels/AppViewModels';
import { brandSecondary } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { Checkbox } from '../../Checkbox';
import { MultiContainerHeader } from '../../MultiContainerHeader';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../autocomplete/SimpleAutoCompleteSearchField';
import { useAddNewTagLogic } from '../../../hooks/useAddNewTagLogic';
import { AddNewTagFooter } from '../../tags/AddNewTagFooter';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IUserSessionComponentProps, IErrorMessageComponentProps {
	className?: string;
	importContacts: AppImportContactsViewModel;
	onNextButtonClicked?(): void;
}

const AcceptedFileExtensions = '.csv,.xlsx,.xls';

const _ContactsSelfImportUpload: React.FC<IProps> = observer(props => {
	const {
		className,
		importContacts,
		userSession,
		logPageView,
		logInput,
		logEvent,
		logApiError,
		errorMessages,
		onNextButtonClicked: onNextButtonClickedProp,
	} = props;

	// State
	const [dataRefresh, setDataRefresh] = React.useState(false);
	const [file, setFile] = React.useState<File | undefined>(undefined);

	// Refs
	const filesInputRef = React.useRef<HTMLInputElement>(null);
	const tagInputRef = React.useRef<HTMLInputElement>(null);

	// Lifecycle
	React.useEffect(() => {
		logPageView('ContactsSelfImport/Upload');
	}, [logPageView]);

	// Shared hook for "Add New Tag" functionality
	const { handleAddNewTag, onTagSearchFieldRef } = useAddNewTagLogic({
		onTagSelected: tagName => {
			importContacts.options.setTags([tagName]);
		},
	});
	const onRenderResultsFooter = React.useCallback(
		(searchQuery: string, renderedItems: any[], totalCount: number) => {
			if (totalCount === 0 && searchQuery) {
				return <AddNewTagFooter searchQuery={searchQuery} onAddTag={handleAddNewTag} />;
			}
			return null;
		},
		[handleAddNewTag]
	);

	// Event Handlers
	const clearSelectedTag = React.useCallback(() => {
		importContacts.options.setTags([]);
		logInput('ClearSearchField', 'Click');
	}, [logInput, importContacts]);

	const onSearchFieldInputKeyDown = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldEvent<React.KeyboardEvent<HTMLInputElement>>) => {
			if (!!e.sourceEvent && e.sourceEvent.keyCode === 13) {
				(e.sourceEvent.target as HTMLInputElement).blur();
			}
		},
		[]
	);

	const onTagSelected = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<string>) => {
			if (e.target) {
				e.target.setSearchQuery(e.selection);
			}
			importContacts.options.setTags(e.selection ? [e.selection] : []);
			logEvent('TagSelected', { tag: e.selection || '' });
		},
		[logEvent, importContacts]
	);

	const onTagInputChanged = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldEvent<React.ChangeEvent<HTMLInputElement>>) => {
			if (e.sourceEvent) {
				const tag = (e.sourceEvent.target as HTMLInputElement).value;
				importContacts.options.setTags(tag ? [tag] : []);
			}
		},
		[importContacts]
	);

	const onChooseFileButtonClicked = React.useCallback(() => {
		if (filesInputRef.current) {
			logInput('ChooseFile', 'Click');
			filesInputRef.current.click();
		}
	}, [logInput]);

	const onFilesInputChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files;
			if (!!files && files.length > 0) {
				const filteredFiles = Array.from(files).filter(x => {
					const comps = x.name ? x.name.split('.').filter(y => !!y) : [];
					const ext = comps.length > 1 ? comps[comps.length - 1] : null;
					return ext ? AcceptedFileExtensions.indexOf(ext.toLocaleLowerCase()) >= 0 : false;
				});

				e.target.value = null;

				if (filteredFiles.length > 0) {
					const selectedFile = filteredFiles[0];
					const fileExt = ((selectedFile ? selectedFile.name : '')
						.split('.')
						.filter(x => !!x)
						.reverse() || [])[0];

					logEvent('FilesAdded', { fileExt });
					setFile(selectedFile);
				}
			}
		},
		[logEvent]
	);

	const onFileDropped = React.useCallback(
		(files: File[]) => {
			if (files.length > 0) {
				const selectedFile = files[0];
				const fileExt = ((selectedFile ? selectedFile.name : '')
					.split('.')
					.filter(x => !!x)
					.reverse() || [])[0];

				logInput('FileDropped', 'Direct', {
					fileExt,
				});
				setFile(selectedFile);
			}
		},
		[logInput]
	);

	const onImportPrivacyOptionChanged = React.useCallback(
		(option: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const visibility = option === 'admin' && !!e.target.checked ? 'admin' : 'all';
			importContacts.options.setVisibility(visibility);
			logEvent('ImportVisibilityChanged', { visibility });
		},
		[importContacts, logEvent]
	);

	const onNextButtonClicked = React.useCallback(() => {
		if (file) {
			const promise = importContacts.importFromFile(file, null, dataRefresh);
			if (promise) {
				logInput('CreatePreview', 'Click');
				promise
					.then(() => {
						if (onNextButtonClickedProp) {
							onNextButtonClickedProp();
						}
					})
					.catch((error: IOperationResultNoValue) => {
						logApiError('CreatePreview-Error', error);
						errorMessages.pushApiError(error);
					});
			}
		}
	}, [file, importContacts, dataRefresh, logInput, onNextButtonClickedProp, logApiError, errorMessages]);

	const onDataRefreshChanged = React.useCallback(() => {
		setDataRefresh(!dataRefresh);
	}, [dataRefresh]);

	// Computed values
	const uploadHint = `Supported file types: ${AcceptedFileExtensions.split(',').reduce((x, y) => {
		return `${x}${x ? ', ' : ''}${y}`;
	}, '')}`;

	return (
		<div className={`${css(styleSheet.container)} contacts-self-import-upload ${className || ''}`}>
			<MultiContainerHeader fullscreenHeader='Import Contacts from Spreadsheet' />
			<div className={css(styleSheet.options)}>
				<div className={css(styleSheet.importOptions)}>
					<div className={css(styleSheet.optionsTitle)}>How would you like the new contacts imported?</div>
					<div>
						<RadioButton
							checked={importContacts.options.visibility === 'all'}
							className={css(styleSheet.importOptionsRadio)}
							id='ContactsSelfImportUpload-Import-Visibility-All'
							name='ContactsSelfImportUpload-Import-Visibility'
							onChange={onImportPrivacyOptionChanged('all')}
						>
							<span>Import contacts with visibility set to all employees</span>
						</RadioButton>
						<RadioButton
							checked={importContacts.options.visibility === 'admin'}
							className={css(styleSheet.importOptionsRadio)}
							id='ContactsSelfImportUpload-Import-Visibility-Admin'
							name='ContactsSelfImportUpload-Import-Visibility'
							onChange={onImportPrivacyOptionChanged('admin')}
						>
							<span>Import contacts with visibility set to private so only I can see them</span>
						</RadioButton>
					</div>
				</div>
				<div className={css(styleSheet.tagOptions)}>
					<div className={css(styleSheet.optionsTitle)}>Preferred Tag:</div>
					<div className={css(styleSheet.optionsSubtitle)}>You can add a tag to everyone you import.</div>
					<SimpleAutoCompleteSearchField
						onChange={onTagInputChanged}
						// @ts-ignore
						onItemSelected={onTagSelected}
						onKeyDown={onSearchFieldInputKeyDown}
						onClear={clearSelectedTag}
						onRenderResultsFooter={onRenderResultsFooter}
						onSearchFieldRef={onTagSearchFieldRef}
						onInputRef={tagInputRef}
						pageSize={10}
						placeholder='Search existing or enter new tag'
						renderNoResultsItem={false}
						resultsLimit={10}
						style={styleSheet.searchField}
						type={ResourceAutoCompleteViewModelType.Tag}
					/>
				</div>

				{userSession.account.preferences.deleteRenewalKeyFacts && (
					<div className={css(styleSheet.importOptionsRadio)}>
						<Checkbox id='data-refresh' onChange={onDataRefreshChanged} checked={dataRefresh}>
							<div>This is a data refresh (clear renewal keyfacts and policy tags)</div>
						</Checkbox>
					</div>
				)}
			</div>
			<div className={css(styleSheet.uploadField)}>
				{importContacts.isBusy ? (
					<LoadingSpinner className='absolute-center' type='large' />
				) : (
					<FilesDropTarget
						className={css(styleSheet.uploadTarget)}
						onFilesDropped={onFileDropped}
						supportedFileExtensions={AcceptedFileExtensions.split(',').map(x => x.slice(1))}
						supportMultiple={true}
						title={uploadHint}
					>
						<div className={css(styleSheet.uploadTargetOverlay)}>
							<SpreadsheetIcon className={css(styleSheet.uploadSpreadsheetIcon)} />
							{file ? (
								<div className={css(styleSheet.uploadTargetOverlayChosenFile)}>{file.name}</div>
							) : (
								<SvgIcon className={css(styleSheet.uploadTargetOverlayArrow)} height={27} width={20}>
									<polygon
										fill={brandSecondary}
										fillRule='evenodd'
										points='20 10.048 10 0 0 10.048 3.004 13.066 7.876 8.172 7.876 26.5 12.124 26.5 12.124 8.172 16.996 13.066'
									/>
								</SvgIcon>
							)}
							<input
								accept={AcceptedFileExtensions}
								id='contacts-self-import-upload-files-input'
								multiple={false}
								onChange={onFilesInputChanged}
								ref={filesInputRef}
								style={{
									height: 0,
									opacity: 0,
									pointerEvents: 'none',
									position: 'fixed',
									visibility: 'hidden',
									width: 0,
									zIndex: -1,
								}}
								type='file'
							/>
							<div className={css(styleSheet.uploadTargetOverlayMessage)}>
								<span>Drop any Excel, CSV, or</span>
								&nbsp;
								<button
									className={css(baseStyleSheet.brandSecondaryLink, styleSheet.uploadTargetOverlayButton)}
									onClick={onChooseFileButtonClicked}
								>
									<span>browse your files</span>
								</button>
							</div>
						</div>
					</FilesDropTarget>
				)}
			</div>
			<div className={css(styleSheet.footer)}>
				<button className={css(baseStyleSheet.ctaButton)} onClick={onNextButtonClicked}>
					<span>Next</span>
				</button>
			</div>
		</div>
	);
});

const ContactsSelfImportUploadWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(_ContactsSelfImportUpload);
export const ContactsSelfImportUpload = withEventLogging(
	ContactsSelfImportUploadWithContext,
	'ContactsSelfImportUpload'
);
